import React, { useState, useEffect, useMemo } from "react";
import ordinal from "ordinal";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_UPCOMING_TRANSFER,
  DELETE_AUTO_FUNDING,
} from "../../../graphql/account";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import {
  RenderTransactionDirection,
  type TransactionDirection,
} from "../../common/RenderTransactionDirection";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import { formatCash, formatCashFull } from "../../../utils/formatCash";
import JobType from "../../../enums/JobType";
import {
  GET_BALANCE_SETTINGS,
  REMOVE_COMPANY_BALANCE_SETTING,
} from "../../../graphql/company";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useFeatureEntitlements } from "@frontegg/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Delete01Icon,
  Infinity01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { fullName } from "utils/fullName";

interface Data {
  id: string;
  transferDate: string;
  accountNumber: string;
  userName: string;
  amount: string;
  type: TransactionDirection;
  schedule: string;
  repeatEndOn: string | JSX.Element;
  memo: string;
}

interface ScheduledTransferFundsProps {
  refreshDataIndex?: number;
}

const AUTO_BALANCE_LABEL = "Auto Balance";

/** The repeat rule behind a scheduled transfer, as the table reads it. */
interface TransferSchedule {
  isRepeatJob?: boolean | null;
  repeatOnDay?: boolean | null;
  repeatDayOfMonth?: number | null;
  repeatWeekday?: number | null;
  repeatDayOfWeek?: number | null;
  repeatNeverEnd?: boolean | null;
  repeatEndOn?: string | number | null;
}

/** Only the fields this table reads off a scheduled-transfer node. */
interface TransferNode {
  id: string;
  date?: string | number | null;
  accountNumber?: string | null;
  amount: number;
  currency?: string | null;
  memo?: string | null;
  type?: string | null;
  schedule: TransferSchedule;
  user?: { firstName?: string | null; lastName?: string | null } | null;
}

/** The query name varies, so the extractor reads the first key. */
type TransferConnection = Record<
  string,
  { edges: { node: TransferNode }[] } | undefined
>;

const ScheduledTransferFunds: React.FC<ScheduledTransferFundsProps> = (
  props,
) => {
  const { refreshDataIndex } = props;
  const { t } = useTranslation();

  // These rows delete company balance settings and scheduled transfers, which
  // is what AccountActionsMenu gates everywhere else. This table pre-dates that
  // gate and was unmounted when it landed, so mounting it here would otherwise
  // hand an unentitled tenant a way around it.
  const { isEntitled: canManageBanks } = useFeatureEntitlements(
    "feature_manage_banks",
  );

  const [combinedData, setCombinedData] = useState<Data[]>([]);
  const [balanceSetting, setBalanceSetting] = useState<Data | null>(null);

  const defaultPaginationVariables = {
    first: DEFAULT_PAGINATION_SIZE,
    after: undefined,
  };

  const [deleteAutoFunding] = useMutation(DELETE_AUTO_FUNDING, {
    onCompleted: () => {
      refetchTransfers();
      toast.success(t("Scheduled Transfer successfully removed"));
    },
    onError: (err) => {
      toast.error(t(err.message));
    },
  });

  const [removeCompanyBalanceSetting] = useMutation(
    REMOVE_COMPANY_BALANCE_SETTING,
    {
      onCompleted: () => {
        refetchTransfers();
        setBalanceSetting(null);
        toast.success(t("Auto Balance successfully removed"));
      },
      onError: (err) => {
        toast.error(t(err.message));
      },
    },
  );

  const getSchedule = (rawSchedule: TransferSchedule) => {
    let schedule = "";
    if (rawSchedule.isRepeatJob) {
      if (rawSchedule.repeatOnDay && rawSchedule.repeatDayOfMonth != null) {
        schedule =
          t("Monthly on the") + " " + ordinal(rawSchedule.repeatDayOfMonth);
      } else if (
        rawSchedule.repeatWeekday != null &&
        rawSchedule.repeatDayOfWeek != null
      ) {
        schedule =
          t("Monthly on the") +
          " " +
          ordinal(rawSchedule.repeatDayOfWeek) +
          " " +
          dayjs().day(rawSchedule.repeatWeekday).format("dddd");
      } else {
        schedule = t("One Time");
      }
    } else {
      schedule = t("One Time");
    }
    return schedule;
  };

  const extractUpcomingTransfers = (data: TransferConnection) => {
    const firstKey = Object.keys(data)[0];
    return (data[firstKey]?.edges ?? []).map((item) => ({
      id: item.node.id,
      transferDate: dayjs(item.node.date).format("MM/DD/YYYY"),
      accountNumber: item.node.accountNumber,
      userName: fullName(item.node.user),
      amount: formatCashFull(item.node.amount, item.node.currency),
      memo: item.node.memo,
      type:
        item.node.type === JobType.FUNDING_SCHEDULE
          ? "DEPOSIT"
          : item.node.type === JobType.TRANSFER_SCHEDULE
            ? "WITHDRAWAL"
            : "UNKNOWN",
      schedule: getSchedule(item.node.schedule),

      repeatEndOn: item.node.schedule.repeatNeverEnd ? (
        <HugeiconsIcon icon={Infinity01Icon} size={16} />
      ) : (
        dayjs(item.node.schedule.repeatEndOn).format("MM/DD/YYYY")
      ),
    }));
  };

  const {
    dataArray: transfers,
    displayLoading,
    isInfiniteScrollFetching,
    handleScroll,
    refetch: refetchTransfers,
  } = useInfiniteScroll({
    query: GET_UPCOMING_TRANSFER,
    initialVariables: defaultPaginationVariables,
    extractData: extractUpcomingTransfers,
    refetchIndex: refreshDataIndex,
  });

  const { loading: loadingBalanceSetting } = useQuery(GET_BALANCE_SETTINGS, {
    fetchPolicy: "cache-and-network",
    variables: { refreshDataIndex },
    onCompleted: (data) => {
      const balanceSetting = data?.getBalanceSettings?.TARGET_BALANCE;
      if (balanceSetting) {
        const newBalanceSetting: Data = {
          id: balanceSetting.toAccountId,
          transferDate: t(AUTO_BALANCE_LABEL),
          accountNumber: balanceSetting.accountNumber,
          userName: fullName(balanceSetting.updatedByUser),
          amount: "-",
          memo: balanceSetting.memo,
          type: "DEPOSIT",
          schedule: `${t("Keeps balance at")} ${formatCash(balanceSetting.targetBalance, balanceSetting.companyCurrency)},
            ${t("Triggers if below")} ${formatCash(balanceSetting.minimumBalance, balanceSetting.companyCurrency)}`,
          repeatEndOn: <HugeiconsIcon icon={Infinity01Icon} size={16} />,
        };
        setBalanceSetting(newBalanceSetting);
      }
    },
  });

  const transferIds = transfers.map((transfer) => transfer.id);

  useEffect(() => {
    if (balanceSetting) {
      setCombinedData([balanceSetting, ...transfers]);
    } else {
      setCombinedData(transfers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceSetting, JSON.stringify(transferIds)]);

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "transferDate",
        header: t("Next Transfer Date"),
        cell: (transfer) => transfer.transferDate,
      },
      {
        id: "accountNumber",
        header: t("Account"),
        cell: (transfer) => transfer.accountNumber,
      },
      {
        id: "userName",
        header: t("Name"),
        cell: (transfer) => transfer.userName,
      },
      {
        id: "amount",
        header: t("Amount"),
        cell: (transfer) => ({ content: transfer.amount, align: "right" }),
      },
      {
        id: "type",
        header: t("Type"),
        cell: (transfer) => (
          <RenderTransactionDirection direction={transfer.type} />
        ),
      },
      {
        id: "schedule",
        header: t("Schedule"),
        cell: (transfer) => transfer.schedule,
      },
      {
        id: "repeatEndOn",
        header: t("Ends On"),
        cell: (transfer) => transfer.repeatEndOn,
      },
      {
        id: "memo",
        header: t("Memo"),
        cell: (transfer) => transfer.memo,
      },
    ],
    [t],
  );

  // Past-dated transfers can no longer be cancelled, so they carry no kebab.
  const rowActions = (transfer: Data): RowAction[] => {
    if (!canManageBanks) return [];

    const isAutoBalance = transfer.transferDate === t(AUTO_BALANCE_LABEL);

    if (isAutoBalance) {
      return [
        {
          label: t("Delete Auto Balance"),
          icon: <HugeiconsIcon icon={Delete01Icon} size={16} />,
          onClick: () => removeCompanyBalanceSetting(),
        },
      ];
    }

    if (
      !dayjs(transfer.transferDate, "MM/DD/YYYY").isAfter(dayjs().endOf("day"))
    ) {
      return [];
    }

    return [
      {
        label: t("Delete Scheduled Transfer"),
        icon: <HugeiconsIcon icon={Delete01Icon} size={16} />,
        onClick: () => deleteAutoFunding({ variables: { id: transfer.id } }),
      },
    ];
  };

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<Data>
        columns={columns}
        rows={combinedData}
        getRowKey={(transfer) => transfer.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onScroll={handleScroll}
        emptyState={t("No scheduled transfers found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more transfers")}
        stickyHeader
      />
      <LoadingWithoutModal open={loadingBalanceSetting} />
    </Box>
  );
};

export default ScheduledTransferFunds;
